import { NgModule } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthInterceptor } from './interceptors/auth.interceptor';

import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { HeaderComponent } from './layout/header/header.component';
import { SidebarComponent } from './layout/sidebar/sidebar.component';
import { FooterComponent } from './layout/footer/footer.component';
import { MainComponent } from './layout/main.component';
import { ToolbarComponent } from './layout/toolbar/toolbar.component';
import { ActivityDrawerComponent } from './layout/drawers/activity-drawer/activity-drawer.component';
import { ChatDrawerComponent } from './layout/drawers/chat-drawer/chat-drawer.component';
import { ShopDrawerComponent } from './layout/drawers/shop-drawer/shop-drawer.component';
import { UpgradePlanModalComponent } from './layout/modals/upgrade-plan-modal/upgrade-plan-modal.component';
import { CreateAppModalComponent } from './layout/modals/create-app-modal/create-app-modal.component';
import { NewTargetModalComponent } from './layout/modals/new-target-modal/new-target-modal.component';
import { ViewUsersModalComponent } from './layout/modals/view-users-modal/view-users-modal.component';
import { UsersSearchModalComponent } from './layout/modals/users-search-modal/users-search-modal.component';
import { InviteFriendsModalComponent } from './layout/modals/invite-friends-modal/invite-friends-modal.component';
import { UserMenuComponent } from './layout/header/user-menu/user-menu.component';
import { LoginComponent } from './pages/auth/login/login.component';
import { ForgotPasswordComponent } from './layout/modals/forgot-password/forgot-password.component';
import { RegisterComponent } from './pages/auth/register/register.component';
import { VerificationComponent } from './pages/auth/verification/verification.component';
import { ResetPasswordComponent } from './pages/auth/reset-password/reset-password.component';
import { AuthHeaderComponent } from './pages/auth/auth-header/auth-header.component';
import { ForbiddenComponent } from './pages/forbidden/forbidden.component';
import { ProformaComponent } from './pages/sales/proforma/proforma.component';
import { SalesComponent } from './pages/sales/sales.component';
import { NegotiationsComponent } from './pages/sales/negotiations/negotiations.component';
import { SalesContractComponent } from './pages/sales/sales-contract/sales-contract.component';
import { InvoiceComponent } from './pages/sales/invoice/invoice.component';
import { PaymentComponent } from './pages/sales/payment/payment.component';
import { ClientManagerComponent } from './pages/sales/client-manager/client-manager.component';
import { CommissionComponent } from './pages/sales/commission/commission.component';
import { ReportComponent } from './pages/sales/report/report.component';
import { MasterDataComponent } from './pages/master-data/master-data.component';
import { MdOtherComponent } from './pages/master-data/md-other/md-other.component';
import { MdVehiclesComponent } from './pages/master-data/md-vehicles/md-vehicles.component';
import { MdInspectionComponent } from './pages/master-data/md-inspection/md-inspection.component';
import { MdVendorsComponent } from './pages/master-data/md-vendors/md-vendors.component';
import { MdCalculatorComponent } from './pages/master-data/md-calculator/md-calculator.component';
import { MdCourierCompanyComponent } from './pages/master-data/md-courier-company/md-courier-company.component';
import { MdBankComponent } from './pages/master-data/md-bank/md-bank.component';
import { MdCurrencyComponent } from './pages/master-data/md-currency/md-currency.component';
import { MdViewMasterComponent } from './pages/master-data/md-view-master/md-view-master.component';
import { VehiclesComponent } from './pages/vehicles/vehicles.component';
import { VhManagerComponent } from './pages/vehicles/vh-manager/vh-manager.component';
import { VhAucnetDataComponent } from './pages/vehicles/vh-aucnet-data/vh-aucnet-data.component';
import { VhInventoryComponent } from './pages/vehicles/vh-inventory/vh-inventory.component';
import { VhCountryStocksComponent } from './pages/vehicles/vh-country-stocks/vh-country-stocks.component';
import { VhBidManagerComponent } from './pages/vehicles/vh-bid-manager/vh-bid-manager.component';
import { VhLiveAuctionComponent } from './pages/vehicles/vh-live-auction/vh-live-auction.component';
import { VhOnePriceDataComponent } from './pages/vehicles/vh-one-price-data/vh-one-price-data.component';
import { VhSaleStatsComponent } from './pages/vehicles/vh-sale-stats/vh-sale-stats.component';
import { VhRefundComponent } from './pages/vehicles/vh-refund/vh-refund.component';
import { CommonComponent } from './pages/common/common.component';
import { CmNewsLetterComponent } from './pages/common/cm-news-letter/cm-news-letter.component';
import { PopularVehiclesComponent } from './pages/common/popular-vehicles/popular-vehicles.component';
import { TitleManagerComponent } from './pages/common/title-manager/title-manager.component';
import { NewsManagerComponent } from './pages/common/news-manager/news-manager.component';
import { FreightManagerComponent } from './pages/common/freight-manager/freight-manager.component';
import { CmInquiriesComponent } from './pages/common/cm-inquiries/cm-inquiries.component';
import { MailManagerComponent } from './pages/common/mail-manager/mail-manager.component';
import { CmAdminUsersComponent } from './pages/common/cm-admin-users/cm-admin-users.component';
import { CmContactsComponent } from './pages/common/cm-contacts/cm-contacts.component';
import { CmTestimonialComponent } from './pages/common/cm-testimonial/cm-testimonial.component';
import { PerformaAddCompanyModalComponent } from './layout/modals/performa-add-company-modal/performa-add-company-modal.component';
import { PerformaBrokerManagerModalComponent } from './layout/modals/performa-broker-manager-modal/performa-broker-manager-modal.component';
import { PerformaAddPortModalComponent } from './layout/modals/performa-add-port-modal/performa-add-port-modal.component';

import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { ProformaListComponent } from './pages/sales/proforma/proforma-list/proforma-list.component';
import { ExportModalComponent } from './layout/modals/export-modal/export-modal.component';
import { ProformaSettingsComponent } from './pages/sales/proforma/proforma-settings/proforma-settings.component';
import { ProformaInvoiceChargesComponent } from './pages/sales/proforma/proforma-invoice-charges/proforma-invoice-charges.component';
import { ProformaManageComponent } from './pages/sales/proforma/proforma-manage/proforma-manage.component';
import { ProformaAddNewComponent } from './pages/sales/proforma/proforma-add-new/proforma-add-new.component';
import { CmRolesListComponent } from './pages/common/cm-admin-users/cm-roles/cm-roles-list/cm-roles-list.component';
import { CommonAddRoleModalComponent } from './layout/modals/common-add-role-modal/common-add-role-modal.component';
import { CommonUpdateRoleModalComponent } from './layout/modals/common-update-role-modal/common-update-role-modal.component';
import { CmUsersListComponent } from './pages/common/cm-admin-users/cm-users/cm-users-list/cm-users-list.component';
import { CmUsersComponent } from './pages/common/cm-admin-users/cm-users/cm-users.component';
import { CmEditPermissionComponent } from './pages/common/cm-admin-users/cm-permissions/cm-edit-permission/cm-edit-permission.component';
import { CmRolesComponent } from './pages/common/cm-admin-users/cm-roles/cm-roles.component';
import { CmPermissionsComponent } from './pages/common/cm-admin-users/cm-permissions/cm-permissions.component';
import { CmPermissionsListComponent } from './pages/common/cm-admin-users/cm-permissions/cm-permissions-list/cm-permissions-list.component';
import { CommonAddPermissionModalComponent } from './layout/modals/common-add-permission-modal/common-add-permission-modal.component';
import { CommonUpdatePermissionModalComponent } from './layout/modals/common-update-permission-modal/common-update-permission-modal.component';
import { CmEditRoleComponent } from './pages/common/cm-admin-users/cm-roles/cm-edit-role/cm-edit-role.component';
import { CmUserDetailComponent } from './pages/common/cm-admin-users/cm-users/cm-user-detail/cm-user-detail.component';
import { UpdateUserDetailModalComponent } from './pages/common/cm-admin-users/cm-users/update-user-detail-modal/update-user-detail-modal.component';
import { AddScheduleModalComponent } from './layout/modals/add-schedule-modal/add-schedule-modal.component';
import { InvoiceListComponent } from './pages/sales/invoice/invoice-list/invoice-list.component';
import { ManageDepositsListComponent } from './pages/sales/payment/manage-deposits-list/manage-deposits-list.component';
import { ManageCrdrNotesListComponent } from './pages/sales/payment/manage-crdr-notes-list/manage-crdr-notes-list.component';
import { PaymentAllocationsComponent } from './pages/sales/payment/payment-allocations/payment-allocations.component';
import { SalesPaymentClientAccountModalComponent } from './layout/modals/sales-payment-client-account-modal/sales-payment-client-account-modal.component';
import { ClientManageListComponent } from './pages/sales/client-manager/client-manage-list/client-manage-list.component';
import { ClientExportListComponent } from './pages/sales/client-manager/client-export-list/client-export-list.component';
import { EditClientModalComponent } from './pages/sales/client-manager/edit-client-modal/edit-client-modal.component';
import { AddUserModalComponent } from './layout/modals/add-user-modal/add-user-modal.component';
import { AddEventModalComponent } from './layout/modals/add-event-modal/add-event-modal.component';
import { ViewEventModalComponent } from './layout/modals/view-event-modal/view-event-modal.component';
import { PaginationComponent } from './shared/components/pagination/pagination.component';
import { SearchableSelectComponent } from './shared/components/searchable-select/searchable-select.component';
import { RelativeDatePipe, LastLoginPipe } from './shared/pipes';
import { DestructorComponent } from './components/destructor/destructor.component';
import { CarListingComponent } from './layout/modals/car-listing/car-listing.component';
import { ProformaOneComponent } from './layout/modals/proforma-one/proforma-one.component';
import { ProformaTwoComponent } from './layout/modals/proforma-two/proforma-two.component';
import { ConsigneeListComponent } from './layout/modals/consignee-list/consignee-list.component';
import { ViewInvoiceComponent } from './layout/modals/view-invoice/view-invoice.component';
import { PlainInvoiceComponent } from './layout/modals/pdf-formats/plain-invoice/plain-invoice.component';
import { CaricomInvoiceComponent } from './layout/modals/pdf-formats/caricom-invoice/caricom-invoice.component';
import { CommercialInvoiceComponent } from './layout/modals/pdf-formats/commercial-invoice/commercial-invoice.component';
import { AddUpdateDepositComponent } from './layout/modals/add-update-deposit/add-update-deposit.component';
import { PipesModule } from './shared/pipes/pipes.module';
import { AddUpdateCreditDebitNotesComponent } from './layout/modals/add-update-credit-debit-notes/add-update-credit-debit-notes.component';
import { InvoiceListingComponent } from './pages/sales/payment/invoice-listing/invoice-listing.component';
import { DepositListingComponent } from './pages/sales/payment/deposit-listing/deposit-listing.component';
import { InvoiceDetailModalComponent } from './layout/modals/invoice-detail-modal/invoice-detail-modal.component';
import { AddSalesCommissionComponent } from './layout/modals/add-sales-commission/add-sales-commission.component';
import { ShippingInformationComponent } from './layout/modals/pdf-formats/shipping-information/shipping-information.component';
import { ExportCertificateComponent } from './layout/modals/pdf-formats/export-certificate/export-certificate.component';
import { InvoiceExportCertificateComponent } from './layout/modals/pdf-formats/invoice-export-certificate/invoice-export-certificate.component';
import { HouseBillLandingComponent } from './layout/modals/pdf-formats/house-bill-landing/house-bill-landing.component';
import { ProformaEditComponent } from './pages/sales/proforma/proforma-edit/proforma-edit.component';
import { ArApComponent } from './pages/sales/report/types/ar-ap/ar-ap.component';
import { PendingInvoicesComponent } from './pages/sales/report/types/pending-invoices/pending-invoices.component';
import { SalesComponent as SalesReportComponent } from './pages/sales/report/types/sales/sales.component';

import { ShipListingComponent } from './pages/vehicles/vh-ship/ship/ship-listing/ship-listing.component';
import { ShipAddEditComponent } from './layout/modals/ship-add-edit/ship-add-edit.component';
import { ShipComponent } from './pages/vehicles/vh-ship/ship/ship.component';
import { UploadFileComponent } from './components/upload-file/upload-file.component';
import { VhManagerListingComponent } from './pages/vehicles/vh-manager/vh-manager-listing/vh-manager-listing.component';
import { VhManagerUpdateComponent } from './layout/modals/vh-manager-update/vh-manager-update.component';
import { VhManagerEditComponent } from './pages/vehicles/vh-manager/vh-manager-edit/vh-manager-edit.component';
import { VehicleUploadDocumentsComponent } from './layout/modals/vehicle-upload-documents/vehicle-upload-documents.component';
import { MdVendorsListingComponent } from './pages/master-data/md-vendors/md-vendors-listing/md-vendors-listing.component';
import { VendorAddEditComponent } from './layout/modals/vendor-add-edit/vendor-add-edit.component';
import { TransportPickupComponent } from './pages/shipping-and-documents/transport-pickup/transport-pickup.component';
import { TransportPickupListingComponent } from './pages/shipping-and-documents/transport-pickup/transport-pickup-listing/transport-pickup-listing.component';
import { TransportPickupAddComponent } from './layout/modals/shipping_and_documents/transport-pickup-add/transport-pickup-add.component';
import { ShippingAndDocumentsComponent } from './pages/shipping-and-documents/shipping-and-documents.component';
import { AuctionsComponent } from './pages/master-data/md-other/auctions/auctions.component';
import { DocumentsComponent } from './pages/master-data/md-other/documents/documents.component';
import { AddEditAuctionHallComponent } from './layout/modals/other/add-edit-auction-hall/add-edit-auction-hall.component';
import { AuctionHallsComponent } from './pages/master-data/md-other/auction-halls/auction-halls.component';
import { MashoManagerComponent } from './pages/shipping-and-documents/masho-manager/masho-manager.component';
import { MashoManagerAddEditComponent } from './layout/modals/shipping_and_documents/masho-manager-add-edit/masho-manager-add-edit.component';
import { MashoManagerListingComponent } from './pages/shipping-and-documents/masho-manager/masho-manager-listing/masho-manager-listing.component';
import { VhManagerVehicleRemarksComponent } from './layout/modals/vh-manager-vehicle-remarks/vh-manager-vehicle-remarks.component';
import { VhManagerUploadImagesComponent } from './layout/modals/vh-manager-upload-images/vh-manager-upload-images.component';
import { VhManagerExportCertificateComponent } from './layout/modals/vh-manager-export-certificate/vh-manager-export-certificate.component';
import { ExportCertificateListingComponent } from './pages/shipping-and-documents/export-certificate/export-certificate-listing/export-certificate-listing.component';
import { ExportCertificateComponent as ExportCertificatePageComponent } from './pages/shipping-and-documents/export-certificate/export-certificate.component';
import { AddEditPurchaseManagerComponent } from './layout/modals/purchase/add-edit-purchase-manager/add-edit-purchase-manager.component';
import { PurchaseComponent } from './pages/purchase/purchase.component';
import { PurchaseManagerComponent } from './pages/purchase/purchase-manager/purchase-manager.component';
import { PurchaseManagerListingComponent } from './pages/purchase/purchase-manager/purchase-manager-listing/purchase-manager-listing.component';
import { ViewExportCertificateComponent } from './layout/modals/shipping_and_documents/view-export-certificate/view-export-certificate.component';
import { AuctionComponent } from './pages/auction/auction.component';
import { SearchBidsComponent } from './pages/auction/search-bids/search-bids.component';
import { ViewBidComponent } from './pages/auction/search-bids/view-bid/view-bid.component';
import { ListingBidComponent } from './pages/auction/search-bids/listing-bid/listing-bid.component';
import { MyBidsComponent } from './pages/auction/my-bids/my-bids.component';
import { VhUpdateBidComponent } from './layout/modals/vh-update-bid/vh-update-bid.component';
import { VhPriceSheetComponent } from './layout/modals/vh-price-sheet/vh-price-sheet.component';
import { VhInventoryDetailComponent } from './layout/modals/vh-inventory-detail/vh-inventory-detail.component';
import { ShippingComponent } from './pages/shipping-and-documents/shipping/shipping.component';
import { CountriesComponent } from './pages/master-data/md-other/countries/countries.component';
import { AddEditCountryComponent } from './layout/modals/add-edit-country/add-edit-country.component';
import { AddEditCourierCompanyComponent } from './layout/modals/add-edit-courier-company/add-edit-courier-company.component';
import { InspectionManagerComponent } from './pages/shipping-and-documents/inspection-manager/inspection-manager.component';
import { AddEditInspectionManagerComponent } from './layout/modals/add-edit-inspection-manager/add-edit-inspection-manager.component';
import { AddEditVehicleRepairComponent } from './layout/modals/add-edit-vehicle-repair/add-edit-vehicle-repair.component';
import { VehicleRepairComponent } from './pages/shipping-and-documents/vehicle-repair/vehicle-repair.component';
import { PortsComponent } from './pages/master-data/md-other/ports/ports.component';
import { AddEditPortComponent } from './layout/modals/add-edit-port/add-edit-port.component';
import { ShippingScheduleComponent } from './pages/shipping-and-documents/shipping-schedule/shipping-schedule.component';
import { SsListingComponent } from './pages/shipping-and-documents/shipping-schedule/ss-listing/ss-listing.component';
import { SsAddComponent } from './pages/shipping-and-documents/shipping-schedule/ss-add/ss-add.component';
import { CourierManagerComponent } from './pages/shipping-and-documents/courier-manager/courier-manager.component';
import { AddEditCourierManagerComponent } from './layout/modals/add-edit-courier-manager/add-edit-courier-manager.component';
import { BulkUpdateCourierManagerComponent } from './layout/modals/bulk-update-courier-manager/bulk-update-courier-manager.component';
import { RegionsComponent } from './pages/master-data/md-other/regions/regions.component';
import { AddEditRegionComponent } from './layout/modals/add-edit-region/add-edit-region.component';
import { VehicleTypesComponent } from './pages/master-data/md-vehicles/vehicle-types/vehicle-types.component';
import { AddEditVehicleTypeComponent } from './layout/modals/md-vehicles/add-edit-vehicle-type/add-edit-vehicle-type.component';
import { BodyTypesComponent } from './pages/master-data/md-vehicles/body-types/body-types.component';
import { AddEditBodyTypeComponent } from './layout/modals/md-vehicles/add-edit-body-type/add-edit-body-type.component';
import { AddEditCarMakersComponent } from './layout/modals/md-vehicles/add-edit-car-makers/add-edit-car-makers.component';
import { MakesComponent } from './pages/master-data/md-vehicles/makes/makes.component';
import { ModelsComponent } from './pages/master-data/md-vehicles/models/models.component';
import { AddEditCarModelsComponent } from './layout/modals/md-vehicles/add-edit-car-models/add-edit-car-models.component';
import { TransmissionComponent } from './pages/master-data/md-vehicles/transmission/transmission.component';
import { AddEditTransmissionComponent } from './layout/modals/md-vehicles/add-edit-transmission/add-edit-transmission.component';
import { FuelComponent } from './pages/master-data/md-vehicles/fuel/fuel.component';
import { AddEditFuelComponent } from './layout/modals/md-vehicles/add-edit-fuel/add-edit-fuel.component';
import { AddEditBodyColorComponent } from './layout/modals/md-vehicles/add-edit-body-color/add-edit-body-color.component';
import { BodyColorComponent } from './pages/master-data/md-vehicles/body-color/body-color.component';
import { AccessoriesComponent } from './pages/master-data/md-vehicles/accessories/accessories.component';
import { AddEditAccessoriesComponent } from './layout/modals/md-vehicles/add-edit-accessories/add-edit-accessories.component';
import { ModelCodesComponent } from './pages/master-data/md-vehicles/model-codes/model-codes.component';
import { AddEditModelCodesComponent } from './layout/modals/md-vehicles/add-edit-model-codes/add-edit-model-codes.component';
import { RelationsComponent } from './pages/master-data/md-vehicles/relations/relations.component';
import { AddEditMdInspectionComponent } from './layout/modals/add-edit-md-inspection/add-edit-md-inspection.component';
import { CalRatingManagerComponent } from './pages/master-data/md-calculator/cal-rating-manager/cal-rating-manager.component';
import { CalAuctionTransportFeeComponent } from './pages/master-data/md-calculator/cal-auction-transport-fee/cal-auction-transport-fee.component';
import { AddEditAuctionTransportFeeComponent } from './layout/modals/cnf/add-edit-auction-transport-fee/add-edit-auction-transport-fee.component';
import { InspectionChargesComponent } from './pages/master-data/md-calculator/inspection-charges/inspection-charges.component';
import { AddEditInspectionChargesComponent } from './layout/modals/cnf/add-edit-inspection-charges/add-edit-inspection-charges.component';
import { CalOtherChargesComponent } from './pages/master-data/md-calculator/cal-other-charges/cal-other-charges.component';
import { CalCustomClearanceComponent } from './pages/master-data/md-calculator/cal-custom-clearance/cal-custom-clearance.component';
import { CalOtherCfChargesComponent } from './pages/master-data/md-calculator/cal-other-cf-charges/cal-other-cf-charges.component';
import { AddEditMdBankComponent } from './layout/modals/add-edit-md-bank/add-edit-md-bank.component';
import { MdAddMasterComponent } from './pages/master-data/md-add-master/md-add-master.component';
import { MdCompanyAgentsComponent } from './pages/master-data/md-company-agents/md-company-agents.component';
import { AddEditCompanyAgentComponent } from './layout/modals/add-edit-company-agent/add-edit-company-agent.component';
import { BlAmendFormComponent } from './layout/modals/bl-amend-form/bl-amend-form.component';
import { BlAmendComponent } from './pages/shipping-and-documents/bl-amend/bl-amend.component';
import { SubscribersListingComponent } from './pages/common/cm-news-letter/subscribers-listing/subscribers-listing.component';
import { NewsLetterListingComponent } from './pages/common/cm-news-letter/news-letter-listing/news-letter-listing.component';
import { UnsubscribersListingComponent } from './pages/common/cm-news-letter/unsubscribers-listing/unsubscribers-listing.component';
import { NewsLetterSettingsComponent } from './pages/common/cm-news-letter/news-letter-settings/news-letter-settings.component';
import { AddEditNewsLetterComponent } from './layout/modals/common/add-edit-news-letter/add-edit-news-letter.component';
import { AddEditPopularVehiclesComponent } from './layout/modals/common/add-edit-popular-vehicles/add-edit-popular-vehicles.component';
import { AddEditNewsManagerComponent } from './layout/modals/common/add-edit-news-manager/add-edit-news-manager.component';
import { RichTextEditorComponent } from './components/rich-text-editor/rich-text-editor.component';
import { SendInquiryReplyComponent } from './layout/modals/send-inquiry-reply/send-inquiry-reply.component';
import { InquirySalesPersonComponent } from './layout/modals/inquiry-sales-person/inquiry-sales-person.component';
import { ShippingLineComponent } from './pages/master-data/md-other/shipping-line/shipping-line.component';
import { AddEditShippingLineComponent } from './layout/modals/other/add-edit-shipping-line/add-edit-shipping-line.component';
import { FreightManagerNewComponent } from './pages/shipping-and-documents/freight-manager-new/freight-manager-new.component';
import { AddEditFreightManagerNewComponent } from './layout/modals/shipping_and_documents/add-edit-freight-manager-new/add-edit-freight-manager-new.component';
import { CopyFreightManagerComponent } from './layout/modals/shipping_and_documents/copy-freight-manager/copy-freight-manager.component';
import { ViewBlFormComponent } from './layout/modals/shipping_and_documents/view-bl-form/view-bl-form.component';
import { PurchaseManagerUploadDocsComponent } from './layout/modals/purchase-manager-upload-docs/purchase-manager-upload-docs.component';
import { BookingAgentsComponent } from './pages/master-data/md-other/booking-agents/booking-agents.component';
import { AddEditBookingAgentComponent } from './layout/modals/other/add-edit-booking-agent/add-edit-booking-agent.component';
import { DummyInvoiceComponent } from './layout/modals/pdf-formats/dummy-invoice/dummy-invoice.component';
import { ProformaVhDetailComponent } from './layout/modals/proforma-vh-detail/proforma-vh-detail.component';
import { ShippingExportFieldsModalComponent } from './layout/modals/shipping-export-fields-modal/shipping-export-fields-modal.component';
import { ProformaDocsReleaseComponent } from './layout/modals/proforma-docs-release/proforma-docs-release.component';
import { ProformaShipRequestComponent } from './layout/modals/proforma-ship-request/proforma-ship-request.component';
import { ViewDocsReleaseDataComponent } from './layout/modals/view-docs-release-data/view-docs-release-data.component';
import { CtnInvoiceComponent } from './layout/modals/pdf-formats/ctn-invoice/ctn-invoice.component';
import { CtnInvoiceExcelComponent } from './layout/modals/pdf-formats/ctn-invoice-excel/ctn-invoice-excel.component';
import { NegotiationEditComponent } from './layout/modals/negotiation-edit/negotiation-edit.component';
import { NegotiationChatComponent } from './layout/modals/negotiation-chat/negotiation-chat.component';
import { AddEditInvoiceChargeComponent } from './layout/modals/add-edit-invoice-charge/add-edit-invoice-charge.component';
import { SavedSearchesComponent } from './pages/auction/saved-searches/saved-searches.component';
import { AddSavedSearchComponent } from './layout/modals/add-saved-search/add-saved-search.component';
import { ProfileComponent } from './pages/user/profile/profile.component';
import { UpdateBidUnitComponent } from './layout/modals/update-bid-unit/update-bid-unit.component';
import { EditCurrencyRateComponent } from './layout/modals/edit-currency-rate/edit-currency-rate.component';
import { SendShippingMailModalComponent } from './layout/modals/send-shipping-mail-modal/send-shipping-mail-modal.component';
import { UpdateCarComponent } from './layout/modals/update-car/update-car.component';
import { CalFobChargesComponent } from './pages/master-data/md-calculator/cal-fob-charges/cal-fob-charges.component';
import { AddEditMdFobChargesComponent } from './layout/modals/add-edit-md-fob-charges/add-edit-md-fob-charges.component';
import { SsoComponent } from './layout/pages/sso/sso.component';
import { SsoLoginComponent } from './pages/auth/sso-login/sso-login.component';
import { MdFaqsComponent } from './pages/master-data/md-faqs/md-faqs.component';
import { AddEditFaqComponent } from './layout/modals/add-edit-faq/add-edit-faq.component';
import { AddEditTestimonialComponent } from './layout/modals/add-edit-testimonial/add-edit-testimonial.component';
import { AddEditTitleManagerComponent } from './layout/modals/add-edit-title-manager/add-edit-title-manager.component';
import { TransportPickupBulkUpdateComponent } from './layout/modals/transport-pickup-bulk-update/transport-pickup-bulk-update.component';
import { CertificateOfOriginComponent } from './layout/modals/pdf-formats/certificate-of-origin/certificate-of-origin.component';
import { InsuranceInvoiceComponent } from './layout/modals/pdf-formats/insurance-invoice/insurance-invoice.component';
import { ClientDetailComponent } from './pages/sales/client-manager/client-detail/client-detail.component';

@NgModule({
  declarations: [
    AppComponent,
    DashboardComponent,
    HeaderComponent,
    SidebarComponent,
    FooterComponent,
    MainComponent,
    ToolbarComponent,
    ActivityDrawerComponent,
    ChatDrawerComponent,
    ShopDrawerComponent,
    UpgradePlanModalComponent,
    CreateAppModalComponent,
    NewTargetModalComponent,
    ViewUsersModalComponent,
    UsersSearchModalComponent,
    InviteFriendsModalComponent,
    UserMenuComponent,
    LoginComponent,
    ForgotPasswordComponent,
    RegisterComponent,
    VerificationComponent,
    ResetPasswordComponent,
    AuthHeaderComponent,
    ForbiddenComponent,
    ProformaComponent,
    SalesComponent,
    NegotiationsComponent,
    SalesContractComponent,
    InvoiceComponent,
    PaymentComponent,
    ClientManagerComponent,
    CommissionComponent,
    ReportComponent,
    MasterDataComponent,
    MdOtherComponent,
    MdVehiclesComponent,
    MdInspectionComponent,
    MdVendorsComponent,
    MdCalculatorComponent,
    MdCourierCompanyComponent,
    MdBankComponent,
    MdCurrencyComponent,
    MdViewMasterComponent,
    VehiclesComponent,
    VhManagerComponent,
    VhAucnetDataComponent,
    VhInventoryComponent,
    VhCountryStocksComponent,
    VhBidManagerComponent,
    VhLiveAuctionComponent,
    VhOnePriceDataComponent,
    VhSaleStatsComponent,
    VhRefundComponent,
    CommonComponent,
    CmNewsLetterComponent,
    PopularVehiclesComponent,
    TitleManagerComponent,
    NewsManagerComponent,
    FreightManagerComponent,
    CmInquiriesComponent,
    MailManagerComponent,
    CmAdminUsersComponent,
    CmContactsComponent,
    CmTestimonialComponent,
    PerformaAddCompanyModalComponent,
    PerformaBrokerManagerModalComponent,
    PerformaAddPortModalComponent,
    ProformaListComponent,
    ExportModalComponent,
    ProformaSettingsComponent,
    ProformaInvoiceChargesComponent,
    ProformaManageComponent,
    ProformaAddNewComponent,
    CmRolesListComponent,
    CommonAddRoleModalComponent,
    CommonUpdateRoleModalComponent,
    CmUsersListComponent,
    CmUsersComponent,
    CmPermissionsComponent,
    CmEditPermissionComponent,
    CmRolesComponent,
    CmPermissionsListComponent,
    CommonAddPermissionModalComponent,
    CommonUpdatePermissionModalComponent,
    CmEditRoleComponent,
    CmUserDetailComponent,
    UpdateUserDetailModalComponent,
    AddScheduleModalComponent,
    InvoiceListComponent,
    ManageDepositsListComponent,
    ManageCrdrNotesListComponent,
    PaymentAllocationsComponent,
    SalesPaymentClientAccountModalComponent,
    ClientManageListComponent,
    ClientExportListComponent,
    EditClientModalComponent,
    AddUserModalComponent,
    AddEventModalComponent,
    ViewEventModalComponent,
    DestructorComponent,
    PaginationComponent,
    SearchableSelectComponent,
    RelativeDatePipe,
    LastLoginPipe,
    CarListingComponent,
    ProformaOneComponent,
    ProformaTwoComponent,
    ConsigneeListComponent,
    ViewInvoiceComponent,
    PlainInvoiceComponent,
    CaricomInvoiceComponent,
    CommercialInvoiceComponent,
    AddUpdateDepositComponent,
    AddUpdateCreditDebitNotesComponent,
    InvoiceListingComponent,
    DepositListingComponent,
    InvoiceDetailModalComponent,
    AddSalesCommissionComponent,
    ShippingInformationComponent,
    ExportCertificateComponent,
    InvoiceExportCertificateComponent,
    HouseBillLandingComponent,
    ProformaEditComponent,
    ArApComponent,
    PendingInvoicesComponent,
    SalesReportComponent,
    ShipComponent,
    ShipListingComponent,
    ShipAddEditComponent,
    UploadFileComponent,
    VhManagerListingComponent,
    VhManagerUpdateComponent,
    VhManagerEditComponent,
    VehicleUploadDocumentsComponent,
    MdVendorsListingComponent,
    VendorAddEditComponent,
    TransportPickupComponent,
    TransportPickupListingComponent,
    TransportPickupAddComponent,
    ShippingAndDocumentsComponent,
    AuctionsComponent,
    DocumentsComponent,
    AddEditAuctionHallComponent,
    AuctionHallsComponent,
    MashoManagerComponent,
    MashoManagerAddEditComponent,
    MashoManagerListingComponent,
    VhManagerVehicleRemarksComponent,
    VhManagerUploadImagesComponent,
    VhManagerExportCertificateComponent,
    ExportCertificateListingComponent,
    ExportCertificatePageComponent,
    AddEditPurchaseManagerComponent,
    PurchaseComponent,
    PurchaseManagerComponent,
    PurchaseManagerListingComponent,
    ViewExportCertificateComponent,
    AuctionComponent,
    SearchBidsComponent,
    ViewBidComponent,
    ListingBidComponent,
    MyBidsComponent,
    VhUpdateBidComponent,
    VhPriceSheetComponent,
    VhInventoryDetailComponent,
    ShippingComponent,
    CountriesComponent,
    AddEditCountryComponent,
    AddEditCourierCompanyComponent,
    InspectionManagerComponent,
    AddEditInspectionManagerComponent,
    AddEditVehicleRepairComponent,
    VehicleRepairComponent,
    PortsComponent,
    AddEditPortComponent,
    ShippingScheduleComponent,
    SsListingComponent,
    SsAddComponent,
    CourierManagerComponent,
    AddEditCourierManagerComponent,
    BulkUpdateCourierManagerComponent,
    RegionsComponent,
    AddEditRegionComponent,
    VehicleTypesComponent,
    AddEditVehicleTypeComponent,
    BodyTypesComponent,
    AddEditBodyTypeComponent,
    AddEditCarMakersComponent,
    MakesComponent,
    ModelsComponent,
    AddEditCarModelsComponent,
    TransmissionComponent,
    AddEditTransmissionComponent,
    FuelComponent,
    AddEditFuelComponent,
    AddEditBodyColorComponent,
    BodyColorComponent,
    AccessoriesComponent,
    AddEditAccessoriesComponent,
    ModelCodesComponent,
    AddEditModelCodesComponent,
    RelationsComponent,
    AddEditMdInspectionComponent,
    CalRatingManagerComponent,
    CalAuctionTransportFeeComponent,
    AddEditAuctionTransportFeeComponent,
    InspectionChargesComponent,
    AddEditInspectionChargesComponent,
    CalOtherChargesComponent,
    CalCustomClearanceComponent,
    CalOtherCfChargesComponent,
    AddEditMdBankComponent,
    MdAddMasterComponent,
    MdCompanyAgentsComponent,
    AddEditCompanyAgentComponent,
    BlAmendFormComponent,
    BlAmendComponent,
    SubscribersListingComponent,
    NewsLetterListingComponent,
    UnsubscribersListingComponent,
    NewsLetterSettingsComponent,
    AddEditNewsLetterComponent,
    AddEditPopularVehiclesComponent,
    AddEditNewsManagerComponent,
    RichTextEditorComponent,
    SendInquiryReplyComponent,
    InquirySalesPersonComponent,
    ShippingLineComponent,
    AddEditShippingLineComponent,
    FreightManagerNewComponent,
    AddEditFreightManagerNewComponent,
    CopyFreightManagerComponent,
    ViewBlFormComponent,
    PurchaseManagerUploadDocsComponent,
    BookingAgentsComponent,
    AddEditBookingAgentComponent,
    DummyInvoiceComponent,
    ProformaVhDetailComponent,
    ShippingExportFieldsModalComponent,
    ProformaDocsReleaseComponent,
    ProformaShipRequestComponent,
    ViewDocsReleaseDataComponent,
    CtnInvoiceComponent,
    CtnInvoiceExcelComponent,
    NegotiationEditComponent,
    NegotiationChatComponent,
    AddEditInvoiceChargeComponent,
    SavedSearchesComponent,
    AddSavedSearchComponent,
    ProfileComponent,
    UpdateBidUnitComponent,
    EditCurrencyRateComponent,
    SendShippingMailModalComponent,
    UpdateCarComponent,
    CalFobChargesComponent,
    AddEditMdFobChargesComponent,
    SsoComponent,
    SsoLoginComponent,
    MdFaqsComponent,
    AddEditFaqComponent,
    AddEditTestimonialComponent,
    AddEditTitleManagerComponent,
    TransportPickupBulkUpdateComponent,
    CertificateOfOriginComponent,
    InsuranceInvoiceComponent,
    ClientDetailComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule, // Added for HTTP requests
    FormsModule,
    ReactiveFormsModule, // Added for reactive forms support
    RouterModule.forRoot([]),
    SweetAlert2Module.forRoot(),
    PipesModule, // Added for custom pipes
  ],
  providers: [
    Title,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
